import React, { useState, useCallback } from 'react';
import { Bell, Lock, Palette, User, Wrench } from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { addEmailToAccount, generateTelegramLink, mergeAccounts } from '../api/user';
import { PushService } from '../services/PushService';
import { cn } from '../utils/cn';
import { Banner, PageHeader, SegmentedControl } from '../components/ui';
import { ProfileSection } from './settings/ProfileSection';
import { SecuritySection } from './settings/SecuritySection';
import { NotificationsSection } from './settings/NotificationsSection';
import { AppearanceSection } from './settings/AppearanceSection';
import { SystemSection } from './settings/SystemSection';

type TabKey = 'profile' | 'security' | 'notifications' | 'appearance' | 'system';

const TABS: { key: TabKey; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { key: 'profile', label: 'Profil', icon: User },
  { key: 'security', label: 'Sicherheit', icon: Lock },
  { key: 'notifications', label: 'Meldungen', icon: Bell },
  { key: 'appearance', label: 'Darstellung', icon: Palette },
  { key: 'system', label: 'System', icon: Wrench },
];

const Settings: React.FC = () => {
  const { user, logout, loadUser } = useAuth();

  const [activeTab, setActiveTab] = useState<TabKey>('profile');

  // Profile form state
  const [email, setEmail] = useState(user?.email || '');

  // Password form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Feed max-age preference (days; 0 = no limit)
  const [feedMaxAgeDays, setFeedMaxAgeDays] = useState<number>(() => {
    const stored = localStorage.getItem('feed_max_age_days');
    return stored !== null ? parseInt(stored, 10) : 0;
  });

  const handleFeedMaxAgeChange = (days: number) => {
    setFeedMaxAgeDays(days);
    if (days === 0) {
      localStorage.removeItem('feed_max_age_days');
    } else {
      localStorage.setItem('feed_max_age_days', String(days));
    }
    setSuccess('Treffer-Einstellung gespeichert.');
    setTimeout(() => setSuccess(''), 2500);
  };

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Add email form state (for Telegram-only users)
  const [addEmail, setAddEmail] = useState('');
  const [addEmailPassword, setAddEmailPassword] = useState('');
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [mergeEmail, setMergeEmail] = useState('');
  const [mergePassword, setMergePassword] = useState('');

  // Telegram link state
  const [telegramDeepLink, setTelegramDeepLink] = useState('');
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      await api.put(`/users/${user?.userId}`, { email });

      await loadUser();
      setSuccess('E-Mail geändert.');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Ändern der E-Mail');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('Passwörter stimmen nicht überein');
      return;
    }

    if (newPassword.length < 8) {
      setError('Neues Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }

    setIsLoading(true);

    try {
      await api.put(`/users/${user?.userId}/password`, {
        currentPassword,
        newPassword,
      });

      setSuccess('Passwort geändert.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Fehler beim Ändern des Passworts');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('Bist du sicher? Dein Konto wird dauerhaft gelöscht.')) {
      return;
    }

    setIsLoading(true);

    try {
      await api.delete(`/users/${user?.userId}`);
      logout();
      window.location.href = '/login';
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Löschen des Kontos');
      setIsLoading(false);
    }
  };

  // Flow A1: Telegram-user adds email
  const handleAddEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      await addEmailToAccount(addEmail, addEmailPassword);
      setSuccess('E-Mail hinzugefügt. Bitte bestätige den Verifizierungslink in deinem Postfach.');
      setAddEmail('');
      setAddEmailPassword('');
      await loadUser();
    } catch (err: any) {
      if (err.response?.status === 409 && err.response?.data?.conflict) {
        // Email belongs to another account — offer merge
        setMergeEmail(addEmail);
        setShowMergeDialog(true);
      } else {
        setError(err.response?.data?.error || 'Fehler beim Hinzufügen der E-Mail');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Flow A2: Merge accounts (email-based source)
  const handleMerge = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await mergeAccounts({
        sourceType: 'email',
        sourceEmail: mergeEmail,
        sourcePassword: mergePassword,
      });
      setShowMergeDialog(false);
      setMergePassword('');
      setSuccess(`Accounts zusammengeführt. ${result.migratedSubscriptions} Suchen übernommen.`);
      await loadUser();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Zusammenführen');
    } finally {
      setIsLoading(false);
    }
  };

  // Flow B1: Generate Telegram deep link
  const handleGenerateTelegramLink = useCallback(async () => {
    setError('');
    setIsGeneratingLink(true);
    try {
      const result = await generateTelegramLink();
      setTelegramDeepLink(result.deepLink);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Generieren des Links');
    } finally {
      setIsGeneratingLink(false);
    }
  }, []);

  const handleRemoveDevice = async (deviceId: string) => {
    if (!window.confirm('Möchtest du dieses Gerät wirklich entfernen?')) return;
    setIsLoading(true);
    try {
      await PushService.removeDevice(deviceId);
      await loadUser(); // Refresh user data to update device list
      setSuccess('Gerät entfernt.');
    } catch {
      setError('Fehler beim Entfernen des Geräts');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAppReload = async () => {
    if (!window.confirm('Möchtest du die App wirklich neu laden? Alle Browser-Push-Registrierungen auf diesem Gerät gehen verloren und müssen danach manuell neu aktiviert werden. Eventuell verbleibende "tote" Geräte solltest du danach aus der Liste unten entfernen.')) {
      return;
    }

    if (navigator.serviceWorker) {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.unregister();
        }
        setSuccess('App-Reload gestartet. Neuladen...');
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } catch {
        window.location.reload(); // Fallback
      }
    } else {
      window.location.reload();
    }
  };

  const handleClearData = async () => {
    if (!window.confirm('Möchtest du wirklich alle lokalen Daten löschen? Du wirst abgemeldet und alle Cache-Daten gehen verloren.')) {
      return;
    }

    setIsLoading(true);
    try {
      // Clear Storage
      localStorage.clear();
      sessionStorage.clear();

      // Clear Cookies
      const cookies = document.cookie.split(";");
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i];
        const eqPos = cookie.indexOf("=");
        const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
        document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
      }

      // Clear IndexedDB
      if (window.indexedDB && window.indexedDB.databases) {
        const dbs = await window.indexedDB.databases();
        for (const db of dbs) {
          if (db.name) window.indexedDB.deleteDatabase(db.name);
        }
      }

      setSuccess('Daten gelöscht. Neuladen...');
      setTimeout(() => {
        window.location.href = '/login';
      }, 1500);
    } catch {
      setError('Fehler beim Löschen der Daten. Bitte versuche es manuell.');
      setIsLoading(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-wide">
      <PageHeader title="Einstellungen" />

      <div className="-mx-4 mt-4 overflow-x-auto px-4 md:hidden">
        <SegmentedControl
          label="Einstellungen"
          value={activeTab}
          onChange={setActiveTab}
          options={TABS.map((tab) => ({ value: tab.key, label: tab.label }))}
        />
      </div>

      <div className="mt-6 grid gap-8 md:grid-cols-[13rem_1fr]">
        <nav aria-label="Einstellungen" className="hidden md:block">
          <ul className="flex flex-col gap-1">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const active = activeTab === tab.key;
              return (
                <li key={tab.key}>
                  <button
                    type="button"
                    onClick={() => setActiveTab(tab.key)}
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-control px-3 py-2 text-left text-sm font-medium text-ink hover:bg-sunken',
                      active && 'bg-brand-soft text-on-brand-soft hover:bg-brand-soft'
                    )}
                  >
                    <Icon className="h-4 w-4" aria-hidden="true" />
                    {tab.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="max-w-narrow">
          {error && (
            <Banner tone="danger" className="mb-4" onDismiss={() => setError('')}>
              {error}
            </Banner>
          )}
          {success && (
            <Banner tone="success" className="mb-4" onDismiss={() => setSuccess('')}>
              {success}
            </Banner>
          )}

          {activeTab === 'profile' && (
            <ProfileSection
              user={user}
              isLoading={isLoading}
              email={email}
              onEmailChange={setEmail}
              onUpdateProfile={handleUpdateProfile}
              addEmail={addEmail}
              onAddEmailChange={setAddEmail}
              addEmailPassword={addEmailPassword}
              onAddEmailPasswordChange={setAddEmailPassword}
              onAddEmail={handleAddEmail}
              telegramDeepLink={telegramDeepLink}
              isGeneratingLink={isGeneratingLink}
              onGenerateTelegramLink={handleGenerateTelegramLink}
              showMergeDialog={showMergeDialog}
              onCloseMergeDialog={() => setShowMergeDialog(false)}
              mergeEmail={mergeEmail}
              mergePassword={mergePassword}
              onMergePasswordChange={setMergePassword}
              onMerge={handleMerge}
            />
          )}

          {activeTab === 'security' && (
            <SecuritySection
              hasEmail={!!user?.email}
              hasTelegram={!!user?.telegramId}
              isLoading={isLoading}
              currentPassword={currentPassword}
              onCurrentPasswordChange={setCurrentPassword}
              newPassword={newPassword}
              onNewPasswordChange={setNewPassword}
              confirmPassword={confirmPassword}
              onConfirmPasswordChange={setConfirmPassword}
              onChangePassword={handleChangePassword}
              onDeleteAccount={handleDeleteAccount}
            />
          )}

          {activeTab === 'notifications' && (
            <NotificationsSection
              devices={user?.devices}
              isLoading={isLoading}
              onRemoveDevice={handleRemoveDevice}
              feedMaxAgeDays={feedMaxAgeDays}
              onFeedMaxAgeChange={handleFeedMaxAgeChange}
            />
          )}

          {activeTab === 'appearance' && <AppearanceSection />}

          {activeTab === 'system' && <SystemSection onAppReload={handleAppReload} onClearData={handleClearData} />}
        </div>
      </div>
    </div>
  );
};

export default Settings;
